import React, { useEffect, useRef, useState } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import {
  IconBell,
  IconCircleCheck,
  IconMessage2,
  IconPlus,
  IconRefresh,
  IconShieldCheck,
  IconUserPlus,
} from '@tabler/icons-react';
import { NotificationDto, NotificationEntityType, NotificationType, Status } from '../../api/model';
import { INBOX_LIMIT, notificationTarget, useNotificationInbox } from '../../hooks/useNotificationInbox';
import styles from './NotificationBell.module.scss';

const STATUS_TYPES: NotificationType[] = [
  NotificationType.TASK_STATUS_CHANGED,
  NotificationType.ACTION_POINT_STATUS_CHANGED,
  NotificationType.CHECKPOINT_STATUS_CHANGED,
];

const typeIcon = (notification: NotificationDto): React.ReactNode => {
  switch (notification.type) {
    case NotificationType.TASK_ASSIGNED:
    case NotificationType.ACTION_POINT_ASSIGNED:
      return <IconUserPlus size={16} />;
    case NotificationType.CHECKPOINT_STATUS_CHANGED:
      return <IconShieldCheck size={16} />;
    case NotificationType.TASK_STATUS_CHANGED:
    case NotificationType.ACTION_POINT_STATUS_CHANGED:
      return notification.status === Status.FINISHED ? <IconCircleCheck size={16} /> : <IconRefresh size={16} />;
    case NotificationType.TASK_CREATED:
    case NotificationType.ACTION_POINT_CREATED:
    case NotificationType.PHASE_CREATED:
      return <IconPlus size={16} />;
    case NotificationType.FEEDBACK_STATUS_CHANGED:
    case NotificationType.FEEDBACK_NEEDS_INFO:
    case NotificationType.FEEDBACK_RESOLVED:
    case NotificationType.FEEDBACK_REJECTED:
    case NotificationType.FEEDBACK_REPLY:
      return <IconMessage2 size={16} />;
    default:
      return <IconRefresh size={16} />;
  }
};

const formatRelativeTime = (value: string | undefined, t: TFunction): string => {
  if (!value) return '';
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return '';
  const minutes = Math.max(0, Math.round((Date.now() - timestamp) / 60_000));
  if (minutes < 1) return t('time.justNow');
  if (minutes < 60) return t('time.minutesAgo', { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return t('time.hoursAgo', { count: hours });
  const days = Math.round(hours / 24);
  return t('time.daysAgo', { count: days });
};

const messageFor = (notification: NotificationDto, t: TFunction): string => {
  const entityName = notification.entityName || t('fallback.unnamedItem');
  const { type } = notification;

  if (!type) return t('types.UNKNOWN', { entityName });

  if (STATUS_TYPES.includes(type)) {
    if (notification.status === Status.FINISHED) {
      return t(`types.${type}_FINISHED`, { entityName });
    }
    const status = notification.status ? t(`status.${notification.status}`) : '';
    return t(`types.${type}`, { entityName, status });
  }

  return t(`types.${type}`, { entityName, defaultValue: t('types.UNKNOWN', { entityName }) });
};

interface NotificationBellProps {
  /** Off for system admins */
  enabled?: boolean;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ enabled = true }) => {
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const { notifications, unreadCount, isLoading, markAsRead, markAllAsRead } = useNotificationInbox({
    enabled,
    isOpen,
  });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelect = (notification: NotificationDto) => {
    markAsRead(notification.id);
    setIsOpen(false);

    if (notification.entityType === NotificationEntityType.FEEDBACK) {
      navigate({
        to: '/feedbacks',
        search: notification.entityId ? { ticket: notification.entityId } : {},
      });
      return;
    }

    const target = notificationTarget(notification);
    if (!target) return;

    navigate({
      to: '/project/$projectId',
      params: { projectId: String(target.projectId) },
      search: { section: target.section, taskId: target.taskId },
    });
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        type="button"
        className={styles.bellButton}
        onClick={() => setIsOpen(prev => !prev)}
        aria-label={t('bell.ariaLabel')}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <IconBell size={20} stroke={1.8} className={styles.bellIcon} />
        {unreadCount > 0 && (
          <span className={styles.badge}>{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className={styles.panel} role="menu">
          <div className={styles.panelHeader}>
            <span className={styles.panelTitle}>{t('bell.panelTitle')}</span>
            {unreadCount > 0 && (
              <button type="button" className={styles.markAllButton} onClick={markAllAsRead}>
                {t('bell.markAllRead')}
              </button>
            )}
          </div>

          <div className={styles.list}>
            {isLoading && notifications.length === 0 ? (
              <div className={styles.empty}>{t('bell.loading')}</div>
            ) : notifications.length === 0 ? (
              <div className={styles.empty}>{t('bell.empty')}</div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification.id}
                  className={`${styles.item} ${!notification.read ? styles.unread : ''}`}
                  role="menuitem"
                  tabIndex={0}
                  onClick={() => handleSelect(notification)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSelect(notification)}
                >
                  <span className={styles.itemIcon}>{typeIcon(notification)}</span>
                  <div className={styles.itemBody}>
                    <span className={styles.itemMessage}>{messageFor(notification, t)}</span>
                    <span className={styles.itemMeta}>
                      {notification.projectName} · {formatRelativeTime(notification.created, t)}
                    </span>
                  </div>
                  {!notification.read && <span className={styles.unreadDot} aria-hidden="true" />}
                </div>
              ))
            )}
          </div>

          {notifications.length >= INBOX_LIMIT && (
            <div className={styles.panelFooter}>{t('bell.capped', { count: INBOX_LIMIT })}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
